import React, { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  TextField,
  Textarea,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import { formatOrgNumber } from "pages/AdminLayout/menuConfig";
import PageModal from "../LocationsPage/PageModal";
import {
  ADMIN_REQUESTS_BASE,
  AdminRequest,
  AdminRequestPage,
  adminRequestsQuery,
  adminRequestStatusInfo,
  errorText,
  formatOslo,
  replyMailto,
  requestInquiryRefresh,
} from "./inquiries";
import styles from "./Inquiries.module.scss";

/*
 * Superadmin: søknader om moské-admin fra appene. «Godkjenn» gir søkeren admin for moskeen
 * (samme som «gi én moské» under Brukere), «Avslå» tar en valgfri begrunnelse. Søkeren får svaret i
 * innboksen i appen.
 */

const PAGE_SIZE = 50;

const statusOptions = [
  { value: "PENDING", label: "Venter" },
  { value: "APPROVED", label: "Godkjent" },
  { value: "REJECTED", label: "Avslått" },
  { value: "", label: "Alle" },
];

const mosqueText = (r: AdminRequest) =>
  r.mosqueName
    ? `${r.mosqueName} (${formatOrgNumber(r.mosqueOrgNr)})`
    : formatOrgNumber(r.mosqueOrgNr);

const AdminRequestsPage: React.FC = () => {
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<AdminRequestPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminRequest | null>(null);
  const [rejecting, setRejecting] = useState<AdminRequest | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = (await executeRef.current(
      adminRequestsQuery({ status, q: query, page: page - 1, size: PAGE_SIZE }),
      "GET",
    )) as AdminRequestPage | null;
    if (result) setData(result);
    setFailed(!result);
    setIsLoading(false);
  }, [status, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const decided = (updated: AdminRequest, text: string) => {
    toast.success(text);
    setSelected(updated);
    requestInquiryRefresh();
    load();
  };

  const approve = async (r: AdminRequest) => {
    const ok = await confirm({
      title: "Godkjenn søknaden?",
      message: (
        <>
          <b>{r.fullName}</b> ({r.userEmail || r.contactEmail}) blir admin for{" "}
          <b>{r.mosqueName || r.mosqueOrgNr}</b> og får beskjed i appen.
          {r.mosqueHasAdmin && " Moskeen har allerede admin."}
        </>
      ),
      confirmLabel: "Godkjenn",
    });
    if (!ok) return;
    setBusy("approve");
    const updated = (await executeRef.current(
      `${ADMIN_REQUESTS_BASE}/${r.id}/approve`,
      "POST",
    )) as AdminRequest | null;
    setBusy(null);
    if (!updated) {
      toast.error(errorText(lastError(), "Kunne ikke godkjenne søknaden."));
      load();
      return;
    }
    decided(
      updated,
      `${r.fullName} er nå admin for ${r.mosqueName || r.mosqueOrgNr}.`,
    );
  };

  const reject = async () => {
    if (!rejecting) return;
    setBusy("reject");
    const updated = (await executeRef.current(
      `${ADMIN_REQUESTS_BASE}/${rejecting.id}/reject`,
      "POST",
      { reason: reason.trim() || null },
    )) as AdminRequest | null;
    setBusy(null);
    if (!updated) {
      toast.error(errorText(lastError(), "Kunne ikke avslå søknaden."));
      return;
    }
    setRejecting(null);
    setReason("");
    decided(updated, "Søknaden er avslått, og søkeren har fått beskjed.");
  };

  const columns: Array<Column<AdminRequest>> = [
    {
      key: "mosque",
      header: "Moské",
      render: (r) => (
        <span className={styles.stack}>
          <b className={styles.clamp}>{r.mosqueName || r.mosqueOrgNr}</b>
          <span className={styles.muted}>
            {formatOrgNumber(r.mosqueOrgNr)}
            {r.mosqueHasAdmin ? " · har admin" : " · ingen admin"}
          </span>
        </span>
      ),
    },
    {
      key: "who",
      header: "Søker",
      render: (r) => (
        <span className={styles.stack}>
          <span className={styles.clamp}>{r.fullName}</span>
          <span className={styles.muted}>{r.role}</span>
        </span>
      ),
    },
    {
      key: "contact",
      header: "Kontakt",
      render: (r) => (
        <span className={styles.stack}>
          <span className={styles.clamp}>{r.contactEmail}</span>
          <span className={styles.muted}>{r.phone}</span>
        </span>
      ),
    },
    {
      key: "user",
      header: "iBønn-bruker",
      render: (r) => <span className={styles.clamp}>{r.userEmail || "–"}</span>,
    },
    {
      key: "time",
      header: "Tid",
      numeric: true,
      render: (r) => formatOslo(r.createdAt),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => {
        const s = adminRequestStatusInfo[r.status];
        return <Badge tone={s?.tone ?? "grey"}>{s?.label ?? r.status}</Badge>;
      },
    },
  ];

  const pageCount = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <>
      <PageHeader
        title="Henvendelser"
        subtitle="Søknader om å bli admin for en moské, sendt fra appen (Profil → Søk om moské-admin)."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={() => {
              load();
              requestInquiryRefresh();
            }}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.split}>
        <Card className={styles.tableCard}>
          <div className={styles.filters}>
            <TextField
              label="Søk"
              placeholder="Søk i navn, e-post, org.nr, rolle eller telefon"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fieldClassName={styles.searchField}
            />
            <Select
              label="Status"
              value={status}
              options={statusOptions}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {failed && !data ? (
            <EmptyState icon="alert" title="Kunne ikke hente søknadene">
              Prøv igjen med «Oppdater».
            </EmptyState>
          ) : isLoading && !data ? (
            <div className={styles.skeletons}>
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} height={44} />
              ))}
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(r) => r.id}
                onRowClick={setSelected}
                rowState={(r) => (r.id === selected?.id ? "on" : undefined)}
                caption="Admin-søknader"
                empty={
                  <EmptyState icon="users" title="Ingen søknader">
                    {status === "PENDING"
                      ? "Ingen søknader venter på svar."
                      : "Ingen søknader passer filtrene."}
                  </EmptyState>
                }
              />
              <div className={styles.footer}>
                <span className={styles.muted}>
                  {data
                    ? `${data.total.toLocaleString("nb-NO")} søknader · ${data.pending.toLocaleString("nb-NO")} venter`
                    : ""}
                </span>
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onChange={setPage}
                />
              </div>
            </>
          )}
        </Card>

        <div className={styles.detail}>
          {selected ? (
            <Card
              title={selected.fullName}
              actions={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelected(null)}
                  aria-label="Lukk"
                >
                  <Icon name="x" size={16} />
                </Button>
              }
            >
              <div className={styles.detailHead}>
                <Badge
                  tone={adminRequestStatusInfo[selected.status]?.tone ?? "grey"}
                >
                  {adminRequestStatusInfo[selected.status]?.label ??
                    selected.status}
                </Badge>
                <span className={styles.muted}>
                  {formatOslo(selected.createdAt)}
                </span>
              </div>

              {selected.status === "PENDING" && (
                <div className={styles.actions}>
                  <Button
                    variant="mint"
                    size="sm"
                    icon={<Icon name="check" size={16} />}
                    loading={busy === "approve"}
                    onClick={() => approve(selected)}
                  >
                    Godkjenn
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={<Icon name="x" size={16} />}
                    onClick={() => {
                      setReason("");
                      setRejecting(selected);
                    }}
                  >
                    Avslå
                  </Button>
                  <a
                    className={`${ui.btn} ${ui.secondary} ${ui.sm}`}
                    href={replyMailto(selected.contactEmail)}
                  >
                    Svar på e-post
                  </a>
                </div>
              )}

              <dl className={styles.details}>
                <dt>Moské</dt>
                <dd>{mosqueText(selected)}</dd>
                <dt>Har admin</dt>
                <dd>
                  {selected.mosqueHasAdmin ? (
                    <span className={styles.warn}>
                      Ja – moskeen har allerede minst én admin
                    </span>
                  ) : (
                    "Nei"
                  )}
                </dd>
                <dt>Rolle</dt>
                <dd>{selected.role}</dd>
                <dt>Telefon</dt>
                <dd>
                  <a href={`tel:${selected.phone.replace(/\s+/g, "")}`}>
                    {selected.phone}
                  </a>
                </dd>
                <dt>Kontakt-e-post</dt>
                <dd>
                  <a href={replyMailto(selected.contactEmail)}>
                    {selected.contactEmail}
                  </a>
                </dd>
                <dt>iBønn-bruker</dt>
                <dd>
                  {selected.userEmail || "–"}
                  <br />
                  <code className={styles.muted}>{selected.firebaseUid}</code>
                </dd>
                {selected.decidedAt && (
                  <>
                    <dt>Behandlet</dt>
                    <dd>
                      {formatOslo(selected.decidedAt)}
                      {selected.decidedBy && ` av ${selected.decidedBy}`}
                    </dd>
                  </>
                )}
                {selected.rejectReason && (
                  <>
                    <dt>Begrunnelse</dt>
                    <dd>{selected.rejectReason}</dd>
                  </>
                )}
              </dl>

              {selected.message ? (
                <p className={styles.body}>{selected.message}</p>
              ) : (
                <p className={styles.muted}>Ingen melding fra søkeren.</p>
              )}
            </Card>
          ) : (
            <Card>
              <EmptyState icon="users" title="Velg en søknad">
                Klikk på en søknad for å se detaljene og godkjenne eller avslå
                den.
              </EmptyState>
            </Card>
          )}
        </div>
      </div>

      <PageModal
        open={!!rejecting}
        title="Avslå søknaden"
        icon="x"
        onClose={() => setRejecting(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              Avbryt
            </Button>
            <Button
              variant="dangerSolid"
              loading={busy === "reject"}
              onClick={reject}
            >
              Avslå
            </Button>
          </>
        }
      >
        {rejecting && (
          <>
            <p>
              <b>{rejecting.fullName}</b> får beskjed i appen om at søknaden til{" "}
              <b>{rejecting.mosqueName || rejecting.mosqueOrgNr}</b> ikke ble
              godkjent.
            </p>
            <Textarea
              label="Begrunnelse (valgfri, sendes til søkeren)"
              value={reason}
              maxChars={500}
              rows={3}
              onChange={(e) => setReason(e.target.value)}
            />
          </>
        )}
      </PageModal>
    </>
  );
};

export default AdminRequestsPage;
